import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Task,
  TaskCreateInput,
  MaterialCreateInput,
  EquipmentCreateInput,
} from '@models/task.model';
import { Equipment } from '@models/equipment.model';
import { Material } from '@models/material.model';
import TasksGraphQL from '@graphql/task.queries';
import {
  graphQlMultipleUpload,
  graphQlUpload,
  graphQlClient,
} from '@config/graphqlClient';
import { formatDateForSubmission } from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';
// import { RootState } from '@redux/store';

interface CreateTaskParams {
  name: string;
  description: string | null;
  taskType: string;
  status: string | null;
  otherType: string | null;
  budget: number | null; // Add budget field
  manHours: number | null;
  startDate: string | null;
  endDate: string | null;
  timeConstraint: number | null;
  selectedTypeConstraint: string | null;
  crewIds?: string[];
  equipmentsIds?: Equipment[]; // Array of Equipment objects (legacy)
  materialsIds?: Material[]; // Array of Material objects (legacy)
  materials?: MaterialCreateInput[]; // New materials creation array
  equipments?: EquipmentCreateInput[]; // New equipments creation array
  deleteDocuments?: string[];
  presedentTaskId?: string[];
  presedentEventId?: string[];
  documents?: File[] | null;
  parentTaskId?: string | null;
  delegateOrganizationId?: string | null;
  rescheduledTask?: Task[];
  note?: string | null;
  superintendentId?: string | null;
  projectId: string;
  subtasks?: {
    add: string[];
    remove: string[];
  };
}

interface UseCreateTaskReturn {
  createTask: (params: CreateTaskParams) => Promise<boolean>;
  createTaskWithData: (params: CreateTaskParams) => Promise<Task | null>;
  loading: boolean;
}

// Create task function
const createTaskAPI = async (params: CreateTaskParams) => {
  const taskData = {
    name: params.name,
    description: params.description,
    taskType: params.taskType,
    // Sprint 49, story-239: no `status` here. The server sets ACTIVE on create; status changes
    // only through the approval/status mutations.
    otherType: params.otherType,
    budget: params.budget,
    manHours: params.manHours,
    startTime: formatDateForSubmission(params.startDate),
    endTime: formatDateForSubmission(params.endDate),
    timeConstraint: params.timeConstraint,
    typeOfTimeConstraint: params.selectedTypeConstraint,
    crewIds: params.crewIds,
    projectId: params.projectId,
    parentTaskId: params.parentTaskId,
    presedentTaskId: params.presedentTaskId,
    presedentEventId: params.presedentEventId,
    deleteDocuments: params.deleteDocuments,
    delegateOrganizationId: params.delegateOrganizationId,
    note: params.status !== 'Rejected' ? null : params.note,
    superintendentId: params.superintendentId,
    materials: params.materials || [],
    equipments: params.equipments || [],
    subtasks: params.subtasks
      ? {
          add: params.subtasks.add || [],
          remove: params.subtasks.remove || [],
        }
      : undefined,
  } as TaskCreateInput;

  const data =
    params.documents && params.documents.length > 0
      ? params.documents.length === 1
        ? await graphQlUpload(
            params.documents[0],
            'task',
            TasksGraphQL.mutations.createTask,
            taskData,
          )
        : await graphQlMultipleUpload(
            params.documents,
            'task',
            TasksGraphQL.mutations.createTask,
            taskData,
          )
      : await graphQlClient.request(TasksGraphQL.mutations.createTask, {
          task: taskData,
        });

  return data;
};

export const useCreateTask = (): UseCreateTaskReturn => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createTaskAPI,
    onSuccess: (data) => {
      if (data?.createTask?.taskId) {
        toast.success(tActionMessages.taskCreatedSuccessfully);

        // Invalidate tasks queries to refresh the task list
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({
          queryKey: ['task', data.createTask.taskId],
        });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.errors[0]?.message || 'Error creating task';
      toast.error(errorMessage);
    },
  });

  const createTask = async (params: CreateTaskParams): Promise<boolean> => {
    try {
      const result = await mutation.mutateAsync(params);
      return !!result?.createTask?.taskId;
    } catch (error) {
      return false;
    }
  };

  const createTaskWithData = async (
    params: CreateTaskParams,
  ): Promise<Task | null> => {
    try {
      const result = await mutation.mutateAsync(params);
      return result?.createTask || null;
    } catch (error) {
      return null;
    }
  };

  return {
    createTask,
    createTaskWithData,
    loading: mutation.isPending,
  };
};
